import { formatFacilityBookingStatus } from '../utils/facilityBookingPresentation'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CalendarPlus, Users } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useMemberActivitiesApi } from '../api/activities'
import GolfDateCarousel from '../components/GolfDateCarousel'
import {
  DEFAULT_GOLF_TIME_ZONE,
  formatMonthDayShort,
  formatWeekdayShort,
  getTodayDateKey,
} from '../utils/golfTime'
import { filterCancelledRosterEntries } from '../../utils/activityFilters'

function formatDateTime(value, locale, timeZone, memberText) {
  if (!value) return memberText('portalPresentation.member_date_to_be_announced')
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(parseISO(value))
  } catch {
    return value
  }
}

export function hasManageAccess(managedActivities, activityId) {
  return managedActivities.some((item) => item.id === activityId)
}

export function getManageActivityTabSections(activeTab) {
  return {
    showSessions: activeTab === 'sessions',
    showAddSession: activeTab === 'add-session',
  }
}

export default function MemberManageActivityDetailPage({ clubCode, managedActivities }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()
  const { activityId } = useParams()
  const {
    listManageSessions,
    createManageSession,
    updateManageSession,
    listManageRoster,
  } = useMemberActivitiesApi()
  const activity = useMemo(() => managedActivities.find((item) => item.id === activityId), [
    activityId,
    managedActivities,
  ])
  const [sessions, setSessions] = useState([])
  const [roster, setRoster] = useState([])
  const [rosterLoading, setRosterLoading] = useState(false)
  const [rosterError, setRosterError] = useState('')
  const [selectedSessionId, setSelectedSessionId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('sessions')
  const [form, setForm] = useState({
    dateKey: null,
    startTime: '',
    endTime: '',
    capacity: '',
    notes: '',
  })

  const resolvedTimeZone = useMemo(() => {
    const activityTimeZone = activity?.time_zone || activity?.timezone || activity?.timeZone
    if (activityTimeZone) return activityTimeZone

    if (typeof window !== 'undefined' && window.__MP_SETTINGS__?.time_zone) {
      return window.__MP_SETTINGS__.time_zone
    }

    if (typeof window !== 'undefined') {
      const rawContext = window.localStorage?.getItem('currentClubContext')
      if (rawContext) {
        try {
          const parsed = JSON.parse(rawContext)
          if (parsed?.time_zone || parsed?.timezone) {
            return parsed.time_zone || parsed.timezone
          }
        } catch (err) {
          // ignore malformed club context
        }
      }
    }

    return DEFAULT_GOLF_TIME_ZONE
  }, [activity])

  const rosterRef = useRef(null)

  const selectedSession = useMemo(
    () => sessions.find((session) => session.id === selectedSessionId),
    [selectedSessionId, sessions],
  )
  const visibleRoster = useMemo(() => filterCancelledRosterEntries(roster), [roster])

  const timeOptions = useMemo(() => {
    const options = []
    for (let i = 0; i < 48; i += 1) {
      const minutes = i * 30
      const hour = Math.floor(minutes / 60)
      const minute = minutes % 60
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
      const label = new Date(2020, 0, 1, hour, minute).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      options.push({ value, label })
    }
    return options
  }, [])

  useEffect(() => {
    if (form.dateKey) return
    setForm((prev) => ({ ...prev, dateKey: getTodayDateKey(resolvedTimeZone) }))
  }, [form.dateKey, resolvedTimeZone])

  // Server expects a timezone-naive club-local timestamp (no "Z"); interpret as club-local time.
  const buildDateTimeString = (dateKey, timeValue) => {
    if (!dateKey || !timeValue) return ''
    return `${dateKey}T${timeValue}:00`
  }

  const loadSessions = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await listManageSessions(clubCode, activityId)
      setSessions(Array.isArray(data) ? data : [])
    } catch (err) {
      setError("portalPresentation.member_unable_to_load_sessions")
      setSessions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!activityId || !hasManageAccess(managedActivities, activityId)) return
    loadSessions()
  }, [activityId, clubCode, listManageSessions, managedActivities])

  useEffect(() => {
    if (!selectedSessionId || !hasManageAccess(managedActivities, activityId)) {
      setRoster([])
      setRosterLoading(false)
      setRosterError('')
      return
    }
    let cancelled = false
    async function loadRoster() {
      setRosterLoading(true)
      setRosterError('')
      try {
        const data = await listManageRoster(clubCode, activityId, selectedSessionId)
        if (!cancelled) setRoster(Array.isArray(data) ? data : [])
      } catch (err) {
        if (!cancelled) {
          setRoster([])
          setRosterError("portalPresentation.member_unable_to_load_roster")
        }
      } finally {
        if (!cancelled) setRosterLoading(false)
      }
    }
    loadRoster()
    return () => {
      cancelled = true
    }
  }, [activityId, clubCode, listManageRoster, selectedSessionId])

  const handleCreateSession = async (event) => {
    event.preventDefault()
    setError('')
    try {
      const starts_at = buildDateTimeString(form.dateKey, form.startTime)
      const ends_at = form.endTime ? buildDateTimeString(form.dateKey, form.endTime) : ''
      await createManageSession(clubCode, activityId, {
        starts_at,
        ends_at: ends_at || null,
        capacity: form.capacity ? Number(form.capacity) : null,
        notes: form.notes,
      })
      setForm({
        dateKey: getTodayDateKey(resolvedTimeZone),
        startTime: '',
        endTime: '',
        capacity: '',
        notes: '',
      })
      await loadSessions()
    } catch (err) {
      setError("portalPresentation.member_unable_to_create_session")
    }
  }

  const handleCancelSession = async (sessionId) => {
    setError('')
    try {
      await updateManageSession(clubCode, activityId, sessionId, { status: 'cancelled' })
      await loadSessions()
    } catch (err) {
      setError("portalPresentation.member_unable_to_update_session")
    }
  }

  const handleViewRoster = (sessionId) => {
    setSelectedSessionId(sessionId)
    requestAnimationFrame(() => {
      rosterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const handleClearRoster = () => {
    setSelectedSessionId('')
    setRoster([])
    setRosterError('')
    setRosterLoading(false)
  }

  if (!hasManageAccess(managedActivities, activityId)) {
    return <div className="mp-panel"> {memberText("portalPresentation.member_you_do_not_have_access_to_this_activity")} </div>
  }

  const { showSessions, showAddSession } = getManageActivityTabSections(activeTab)

  return (
    <div className="mp-panel">
      <div className="mp-panel__header">
        <div>
          <p className="mp-eyebrow"> {memberText("golf.course.manage")} </p>
          <h4 className="mp-panel__title">{activity.name}</h4>
        </div>
      </div>

      {error ? <div className="mp-panel__body">{memberText(error)}</div> : null}

      <div className="mp-panel__body mp-manage-detail">
        <div className="mp-view-toggle">
          <button
            type="button"
            className={`mp-ghost ${activeTab === 'sessions' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          > {memberText("portalPresentation.member_sessions_caption")} </button>
          <button
            type="button"
            className={`mp-ghost ${activeTab === 'add-session' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('add-session')}
          > {memberText("portalPresentation.member_add_session")} </button>
        </div>

        {showSessions ? (
          <>
            <div className="mp-manage-section">
              <div className="mp-manage-section__header">
                <h5 className="mp-panel__subtitle"> {memberText("portalPresentation.member_sessions_caption")} </h5>
              </div>
              {loading ? (
                <p className="mp-text-muted"> {memberText("portalPresentation.member_loading_sessions")} </p>
              ) : sessions.length === 0 ? (
                <div className="mp-empty"> {memberText("portalPresentation.member_no_sessions_yet")} </div>
              ) : (
                <div className="mp-session-grid">
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`mp-session-card ${
                        selectedSessionId === session.id ? 'mp-session-card--selected' : ''
                      }`}
                    >
                      <div className="mp-session-card__header">
                        <p className="mp-session-card__title">{formatDateTime(session.starts_at, locale, resolvedTimeZone, memberText)}</p>
                        <span
                          className={`mp-status-badge ${
                            session.status === 'cancelled' ? 'mp-status-badge--muted' : 'mp-status-badge--active'
                          }`}
                        >
                          {session.status === 'cancelled' ? memberText("portalPresentation.member_cancelled") : memberText("portalPresentation.member_active")}
                        </span>
                      </div>
                      <div className="mp-session-card__meta">
                        <span className="mp-pill muted">{session.booked_count} {memberText("portalPresentation.member_booked")} </span>
                        {session.capacity ? <span className="mp-pill muted">{session.capacity} {memberText("portalPresentation.member_capacity")} </span> : null}
                      </div>
                      <div className="mp-session-card__actions">
                        <button
                          type="button"
                          className="mp-btn mp-btn--outline mp-btn--sm"
                          onClick={() => handleCancelSession(session.id)}
                          disabled={session.status === 'cancelled'}
                        > {memberText("portalPresentation.member_cancel")} </button>
                        <button
                          type="button"
                          className="mp-btn mp-btn--primary mp-btn--sm"
                          onClick={() => handleViewRoster(session.id)}
                        >
                          {selectedSessionId === session.id ? memberText("portalPresentation.member_viewing_roster") : memberText("portalPresentation.member_view_roster")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mp-manage-section" ref={rosterRef}>
              <div className="mp-manage-section__header">
                <h5 className="mp-panel__subtitle">
                  {selectedSession ? memberText('portalPresentation.member_roster') + ' · ' + formatDateTime(selectedSession.starts_at, locale, resolvedTimeZone, memberText) : memberText("portalPresentation.member_roster")}
                </h5>
                {selectedSession ? (
                  <button type="button" className="mp-btn mp-btn--outline mp-btn--sm" onClick={handleClearRoster}> {memberText("portalPresentation.member_clear")} </button>
                ) : null}
              </div>
              {selectedSessionId ? (
                rosterLoading ? (
                  <p className="mp-text-muted"> {memberText("portalPresentation.member_loading_roster")} </p>
                ) : rosterError ? (
                  <div className="mp-empty"> {memberText("portalPresentation.member_unable_to_load_roster")} </div>
                ) : visibleRoster.length === 0 ? (
                  <div className="mp-empty"> {memberText("portalPresentation.member_no_bookings_yet")} </div>
                ) : (
                  <div className="mp-roster-grid">
                    {visibleRoster.map((entry) => (
                      <div key={entry.id} className="mp-roster-card">
                        <div>
                          <p className="mp-roster-card__title">
                            {entry.member_name || memberText("portalPresentation.member_member_caption")}{' '}
                            {entry.member_email ? <span>({entry.member_email})</span> : null}
                          </p>
                          <div className="mp-roster-card__meta">
                            <span className="mp-status-badge mp-status-badge--muted">{formatFacilityBookingStatus(entry.status, memberText)}</span>
                          </div>
                        </div>
                        <div className="mp-roster-card__meta">
                          <Users size={14} /> {formatDateTime(entry.booked_at, locale, resolvedTimeZone, memberText)}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <div className="mp-empty"> {memberText("portalPresentation.member_select_a_session_to_view_the_roster")} </div>
              )}
            </div>
          </>
        ) : null}

        {showAddSession ? (
          <div className="mp-manage-section mp-manage-section--form">
            <div className="mp-manage-section__header">
              <h5 className="mp-panel__subtitle"> {memberText("portalPresentation.member_add_session")} </h5>
              <p className="mp-text-muted"> {memberText("portalPresentation.member_plan_your_next_experience_with_precision_and_ease")} </p>
            </div>
            <form className="mp-form-card" onSubmit={handleCreateSession}>
              <div className="mp-form-card__block">
                <p className="mp-form-card__label"> {memberText("portalPresentation.member_date")} </p>
                <GolfDateCarousel
                  bookingWindow={365}
                  timeZone={resolvedTimeZone}
                  selectedDateKey={form.dateKey}
                  onSelectDate={(dateKey) => setForm((prev) => ({ ...prev, dateKey }))}
                  formatPrimary={(dateKey) => formatWeekdayShort(dateKey, resolvedTimeZone, locale)}
                  formatSecondary={(dateKey) => formatMonthDayShort(dateKey, resolvedTimeZone, locale)}
                />
              </div>
              <div className="mp-form-card__grid">
                <label className="mp-field">
                  <span className="mp-field__label"> {memberText("portalPresentation.member_start_time")} </span>
                  <select
                    value={form.startTime}
                    onChange={(event) => setForm((prev) => ({ ...prev, startTime: event.target.value }))}
                    required
                  >
                    <option value=""> {memberText("portalPresentation.member_select_time")} </option>
                    {timeOptions.map((option) => (
                      <option key={`start-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="mp-field">
                  <span className="mp-field__label"> {memberText("portalPresentation.member_end_time")} </span>
                  <select
                    value={form.endTime}
                    onChange={(event) => setForm((prev) => ({ ...prev, endTime: event.target.value }))}
                  >
                    <option value=""> {memberText("portalPresentation.member_optional")} </option>
                    {timeOptions.map((option) => (
                      <option key={`end-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="mp-field">
                  <span className="mp-field__label"> {memberText("portalPresentation.member_capacity")} </span>
                  <input
                    type="number"
                    placeholder={memberText("portalPresentation.member_optional")}
                    value={form.capacity}
                    onChange={(event) => setForm((prev) => ({ ...prev, capacity: event.target.value }))}
                    min="1"
                  />
                </label>
                <label className="mp-field">
                  <span className="mp-field__label"> {memberText("portalPresentation.member_notes")} </span>
                  <input
                    type="text"
                    placeholder={memberText("portalPresentation.member_add_a_concierge_note")}
                    value={form.notes}
                    onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                  />
                </label>
              </div>
              <button type="submit" className="mp-btn mp-btn--primary mp-btn--sm mp-form-card__submit">
                <CalendarPlus size={14} /> {memberText("portalPresentation.member_add_session")} </button>
            </form>
          </div>
        ) : null}
      </div>
    </div>
  )
}
